import { FC, FormEvent, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Pagination,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import CaresPageHeader from '../../shell/cares-page-header';
import type { AdminSection } from '../config';
import './users-access-page.scss';

type AdminUsersAccessPageProps = {
  section: AdminSection;
  ownerEmail: string | undefined;
};

type PlatformAdminRecord = {
  id: number;
  granted_at: string | null;
  granted_by: {
    name: string;
    email: string;
  } | null;
  can_revoke: boolean;
  lockout_guard_reason: string | null;
  user: {
    id: number;
    name: string;
    first_name: string;
    last_name: string;
    email: string;
    status: 'active' | 'pending_invite' | 'inactive';
    email_verified_at: string | null;
    invited_at: string | null;
    last_login_at: string | null;
    is_break_glass_local: boolean;
  } | null;
};

type PlatformAdminIndexResponse = {
  summary: {
    total: number;
    active: number;
    pending_invite: number;
    break_glass: number;
    filtered_total: number;
  };
  data: {
    data: PlatformAdminRecord[];
    current_page: number;
    last_page: number;
    total: number;
  };
};

type LoadState = 'loading' | 'ready' | 'error';

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const statusToneMap: Record<string, string> = {
  active: 'success',
  pending_invite: 'warning',
  inactive: 'secondary',
};

const formatDateTime = (value: string | null) => {
  if (!value) {
    return 'Not yet';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const formatStatusLabel = (value: string | null | undefined) => (value ?? 'inactive').replace('_', ' ');

const AdminUsersAccessPage: FC<AdminUsersAccessPageProps> = ({ section, ownerEmail }) => {
  const [records, setRecords] = useState<PlatformAdminRecord[]>([]);
  const [summary, setSummary] = useState<PlatformAdminIndexResponse['summary'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [submitting, setSubmitting] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState<number | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [inviteEmail, setInviteEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const applyLoadedRecords = (payload: PlatformAdminIndexResponse) => {
    setRecords(payload.data.data);
    setSummary(payload.summary);
    setPage(payload.data.current_page);
    setLastPage(payload.data.last_page);
    setLoadState('ready');
  };

  const loadRecords = async (targetPage = page) => {
    const params = new URLSearchParams();
    params.set('page', String(targetPage));
    params.set('per_page', '8');
    if (searchTerm.trim() !== '') {
      params.set('search', searchTerm.trim());
    }
    if (statusFilter !== 'all') {
      params.set('status', statusFilter);
    }

    const response = await fetch(`/api/v1/secure/admin/platform-users?${params.toString()}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Unable to load platform admins (${response.status}).`);
    }

    return (await response.json()) as PlatformAdminIndexResponse;
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setLoadState('loading');
      setError(null);

      try {
        const payload = await loadRecords(page);

        if (!cancelled) {
          applyLoadedRecords(payload);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load platform admins.');
          setRecords([]);
          setSummary(null);
          setLastPage(1);
          setLoadState('error');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [page, searchTerm, statusFilter, reloadKey]);

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    setSearchTerm(searchInput);
  };

  const handleInviteSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/v1/secure/admin/platform-users', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          email: inviteEmail.trim(),
        }),
      });

      const payload = (await response.json()) as {
        message?: string;
        record?: PlatformAdminRecord;
        errors?: Record<string, string[]>;
        granted?: boolean;
        created_pending_user?: boolean;
      };

      if (response.status === 422) {
        const fieldMessage = payload.errors?.email?.[0];
        throw new Error(fieldMessage ?? payload.message ?? 'Unable to add platform admin.');
      }

      if (!response.ok) {
        throw new Error(payload.message ?? `Unable to add platform admin (${response.status}).`);
      }

      applyLoadedRecords(await loadRecords(1));
      setInviteEmail('');
      setSuccess(payload.message ?? 'Platform admin updated.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to add platform admin.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (record: PlatformAdminRecord) => {
    if (!record.user) {
      return;
    }

    if (!record.can_revoke) {
      setError(record.lockout_guard_reason ?? 'This admin cannot be revoked.');
      return;
    }

    if (!window.confirm(`Revoke platform admin access for ${record.user.email}?`)) {
      return;
    }

    setRevokingUserId(record.user.id);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`/api/v1/secure/admin/platform-users/${record.user.id}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
      });

      const payload = (await response.json()) as {
        message?: string;
      };

      if (!response.ok) {
        throw new Error(payload.message ?? `Unable to revoke platform admin (${response.status}).`);
      }

      applyLoadedRecords(await loadRecords(records.length === 1 && page > 1 ? page - 1 : page));
      setSuccess('Platform admin access revoked.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to revoke platform admin.');
    } finally {
      setRevokingUserId(null);
    }
  };

  const hasSearchFilter = searchTerm.trim() !== '' || statusFilter !== 'all';
  const showLoadErrorState = !loading && loadState === 'error' && records.length === 0;
  const showFilteredEmptyState = !loading && loadState === 'ready' && records.length === 0 && hasSearchFilter;
  const showTrueEmptyState = !loading && loadState === 'ready' && records.length === 0 && !hasSearchFilter;

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setStatusFilter('all');
    setPage(1);
  };

  return (
    <>
      <CaresPageHeader homepage="CARES Administration" activepage="Admin" page={section.navLabel} />

      <div className="users-access-page">
              {error && !showLoadErrorState ? (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              ) : null}

              {success ? (
                <Alert variant="success" className="mb-4">
                  {success}
                </Alert>
              ) : null}

              <Row className="g-4">
                <Col xl={12}>
                  <Card className="custom-card border shadow-none mb-0">
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                      <div>
                        <div className="card-title mb-1">Current Platform Admins</div>
                        <p className="text-muted fs-12 mb-0">
                          Search by name or email and filter active versus pending invite records.
                        </p>
                      </div>
                      <Form className="users-access-page__filters" onSubmit={handleSearchSubmit}>
                        <Form.Select
                          aria-label="Filter platform admins by status"
                          className="users-access-page__filter-status"
                          value={statusFilter}
                          onChange={(event) => {
                            setPage(1);
                            setStatusFilter(event.target.value);
                          }}
                        >
                          <option value="all">All statuses</option>
                          <option value="active">Active</option>
                          <option value="pending_invite">Pending invite</option>
                          <option value="inactive">Inactive</option>
                        </Form.Select>
                        <Form.Control
                          className="users-access-page__filter-search"
                          aria-label="Search platform admins by name or email"
                          value={searchInput}
                          onChange={(event) => setSearchInput(event.target.value)}
                          placeholder="Search admin"
                        />
                        <Button type="submit" variant="primary" className="users-access-page__filter-submit">
                          Search
                        </Button>
                      </Form>
                    </Card.Header>
                    <Card.Body>
                      {showLoadErrorState ? (
                        <>
                          <Alert variant="danger" className="mb-4">
                            {error ?? 'Unable to load platform admins.'}
                          </Alert>
                          <div className="rounded-3 border border-danger-subtle bg-danger-transparent text-center p-4 p-lg-5">
                            <span className="avatar avatar-xl bg-danger-transparent text-danger mb-3">
                              <i className="ri-error-warning-line fs-24"></i>
                            </span>
                            <h6 className="mb-2">Platform admin access could not be loaded</h6>
                            <p className="text-muted mb-3">
                              The current Platform Admin list is unavailable right now. Retry the request to restore the
                              access table before making changes.
                            </p>
                            <Button variant="danger" onClick={() => setReloadKey((current) => current + 1)}>
                              Retry load
                            </Button>
                          </div>
                        </>
                      ) : loading ? (
                        <div className="py-5 text-center text-muted">
                          <Spinner animation="border" className="me-2" />
                          Loading platform admins...
                        </div>
                      ) : showFilteredEmptyState ? (
                        <div className="rounded-3 border border-dashed text-center p-4 p-lg-5">
                          <span className="avatar avatar-xl bg-info-transparent text-info mb-3">
                            <i className="ri-search-line fs-24"></i>
                          </span>
                          <h6 className="mb-2">No platform admin assignments matched the current filters</h6>
                          <p className="text-muted mb-3">
                            Adjust the search term or status filter to see more platform admin assignments.
                          </p>
                          <Button variant="primary-light" onClick={clearFilters}>
                            Clear filters
                          </Button>
                        </div>
                      ) : showTrueEmptyState ? (
                        <div className="rounded-3 border border-dashed text-center p-4 p-lg-5">
                          <span className="avatar avatar-xl bg-primary-transparent text-primary mb-3">
                            <i className="ri-user-add-line fs-24"></i>
                          </span>
                          <h6 className="mb-2">No platform admin assignments exist yet</h6>
                          <p className="text-muted mb-0">
                            Grant platform admin access from the form on this page to create the first assignment.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive users-access-page__table-wrap">
                            <Table className="table align-middle mb-0 users-access-page__table">
                              <thead>
                                <tr>
                                  <th>Admin</th>
                                  <th>Status</th>
                                  <th>Last Login</th>
                                  <th>Granted By</th>
                                  <th className="text-end">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {records.map((record) => (
                                  <tr key={record.id}>
                                    <td data-label="Admin">
                                      <div className="fw-semibold">{record.user?.name ?? 'Unknown user'}</div>
                                      <div className="text-muted fs-12">{record.user?.email ?? 'No email'}</div>
                                      {record.user?.is_break_glass_local ? (
                                        <Badge bg="" className="bg-info-transparent text-info mt-2">
                                          Break glass local
                                        </Badge>
                                      ) : null}
                                    </td>
                                    <td data-label="Status">
                                      <Badge bg="" className={`bg-${statusToneMap[record.user?.status ?? 'inactive'] ?? 'secondary'}-transparent text-${statusToneMap[record.user?.status ?? 'inactive'] ?? 'secondary'}`}>
                                        {formatStatusLabel(record.user?.status)}
                                      </Badge>
                                      <div className="text-muted fs-12 mt-2">
                                        Invited: {formatDateTime(record.user?.invited_at ?? null)}
                                      </div>
                                    </td>
                                    <td data-label="Last Login">
                                      <div className="fw-medium">{formatDateTime(record.user?.last_login_at ?? null)}</div>
                                      <div className="text-muted fs-12">
                                        {record.user?.email_verified_at ? 'Email verified' : 'Email not verified'}
                                      </div>
                                    </td>
                                    <td data-label="Granted By">
                                      <div className="fw-medium">{record.granted_by?.name ?? 'System seed'}</div>
                                      <div className="text-muted fs-12">{record.granted_by?.email ?? 'Seeder / unknown'}</div>
                                      <div className="text-muted fs-12 mt-1">
                                        Granted: {formatDateTime(record.granted_at)}
                                      </div>
                                    </td>
                                    <td data-label="Action" className="text-end users-access-page__action-cell">
                                      <Button
                                        variant={record.can_revoke ? 'outline-danger' : 'outline-secondary'}
                                        className="users-access-page__revoke-button"
                                        disabled={!record.can_revoke || revokingUserId === record.user?.id}
                                        onClick={() => handleRevoke(record)}
                                      >
                                        {revokingUserId === record.user?.id ? (
                                          <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Revoking...
                                          </>
                                        ) : (
                                          'Revoke'
                                        )}
                                      </Button>
                                      {!record.can_revoke && record.lockout_guard_reason ? (
                                        <div className="text-muted fs-12 mt-2">{record.lockout_guard_reason}</div>
                                      ) : null}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>

                          {lastPage > 1 ? (
                            <div className="d-flex justify-content-end mt-4 users-access-page__pagination">
                              <Pagination className="mb-0">
                                <Pagination.Prev
                                  disabled={page <= 1}
                                  onClick={() => setPage((currentPage) => Math.max(currentPage - 1, 1))}
                                />
                                {Array.from({ length: lastPage }, (_, index) => index + 1).map((pageNumber) => (
                                  <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === page}
                                    onClick={() => setPage(pageNumber)}
                                  >
                                    {pageNumber}
                                  </Pagination.Item>
                                ))}
                                <Pagination.Next
                                  disabled={page >= lastPage}
                                  onClick={() => setPage((currentPage) => Math.min(currentPage + 1, lastPage))}
                                />
                              </Pagination>
                            </div>
                          ) : null}
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={12}>
                  <Card className="custom-card border shadow-none mb-0">
                    <Card.Header>
                      <div className="card-title">Add Platform Admin</div>
                    </Card.Header>
                    <Card.Body>
                      <p className="text-muted">
                        Enter an email address. Existing active users gain admin access immediately.
                        New emails create a pending admin user record plus the role assignment.
                      </p>
                      <Form onSubmit={handleInviteSubmit}>
                        <Row className="g-3 align-items-end">
                          <Col lg={9}>
                            <Form.Group>
                              <Form.Label htmlFor="platform-admin-email">Email address</Form.Label>
                              <Form.Control
                                id="platform-admin-email"
                                name="email"
                                type="email"
                                value={inviteEmail}
                                placeholder="admin@hospital.org"
                                aria-label="Platform admin email address"
                                onChange={(event) => setInviteEmail(event.target.value)}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col lg={3}>
                            <Button type="submit" disabled={submitting} className="w-100">
                              {submitting ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  Saving...
                                </>
                              ) : (
                                'Grant Platform Admin'
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form>

                      <Alert variant="light" className="border mt-4 mb-0">
                        Removing the last remaining platform admin is blocked server-side to avoid lockout.
                      </Alert>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
      </div>
    </>
  );
};

export default AdminUsersAccessPage;
